import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ConnectionManager } from '../modules/connection';
import { ChatManager } from '../modules/chat';
import MessageBubble from '../components/MessageBubble';
import EmojiPicker from '../components/EmojiPicker';
import TypingIndicator from '../components/TypingIndicator';
import FileTransfer from '../components/FileTransfer';
import VideoCall from '../components/VideoCall';
import ScreenShare from '../components/ScreenShare';
import NetworkMonitor from '../components/NetworkMonitor';
import UserList from '../components/UserList';
import SharePopup from '../components/SharePopup';
import ImagePreviewModal from '../components/ImagePreviewModal';
import BackgroundSettingsPanel from '../components/BackgroundSettingsPanel';
import { showToast, showConfirm } from '../utils/notifications';
import { getBgSettings } from '../utils/backgroundSettings';
import { debounce, playSound } from '../utils/helpers';
import { haveLink } from '../utils/helpers';
import AdSenseUnit from '../components/AdSenseUnit';
import '../styles/ChatPage.css';

export default function ChatPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  
  const [connectionManager, setConnectionManager] = useState(null);
  const [chatManager, setChatManager] = useState(null);
  const [peerId, setPeerId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [videoCallPosition, setVideoCallPosition] = useState('top');
  const [showScreenShare, setShowScreenShare] = useState(false);
  const [, setIsInitiator] = useState(false);
  const [roomLink, setRoomLink] = useState('');
  const [userName, setUserName] = useState('');
  const [mediaGallery, setMediaGallery] = useState([]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [showNameSavedTick, setShowNameSavedTick] = useState(false);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [activeTab, setActiveTab] = useState('media');
  const [storedFiles, setStoredFiles] = useState({ media: [], links: [], files: [] });
  const [videoThumbnails, setVideoThumbnails] = useState({});
  
  // Calculate counts for Media/Links/Files buttons
  const mediaCount = storedFiles.media.length;
  const linkCount = storedFiles.links.length;
  const fileCount = storedFiles.files.length;
  const [linkPreview, setLinkPreview] = useState(null);
  const [, setIsFetchingPreview] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [roomPhrase, setRoomPhrase] = useState('');
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [imagePreviewIndex, setImagePreviewIndex] = useState(0);
  const [bgSettings, setBgSettings] = useState(getBgSettings);
  const [showBgSettings, setShowBgSettings] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messageInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const chatManagerRef = useRef(null);
  const linkPreviewTimeoutRef = useRef(null);
  const connectionManagerRef = useRef(null);
  const initializingRef = useRef(false);
  const videoCallRef = useRef(null);
  const screenShareRef = useRef(null);
  const prevUsersRef = useRef([]);
  const userNameInputRef = useRef(null);
  const mobileNameInputRef = useRef(null);

  const [callState, setCallState] = useState({
    isCallActive: false,
    isOnCall: false,
    groupCallActive: false,
    groupCallType: null,
    videoEnabled: false,
    audioEnabled: false,
    hasLocalStream: false
  });
  const [bandwidth, setBandwidth] = useState({ sent: 0, received: 0 });
  const [screenState, setScreenState] = useState({ isSharing: false, hasLocalStream: false, remoteCount: 0 });

  // Auto-hide video panel when call ends
  useEffect(() => {
    const shouldHide = !callState.isCallActive && !callState.isOnCall && !callState.groupCallActive && !callState.showJoinButton;
    if (shouldHide) {
      // Reset audio/video states when call ends
      setCallState(prev => ({
        ...prev,
        videoEnabled: false,
        audioEnabled: false
      }));
      // iOS-specific handling to ensure video panel hides
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      if (isIOS) {
        // Force state update on iOS with a longer delay + double toggle for reliability
        setShowVideoCall(false);
        setTimeout(() => {
          setShowVideoCall(false);
          // Force a re-render by toggling callState
          setCallState(prev => ({ ...prev }));
        }, 300);
      } else {
        setShowVideoCall(false);
      }
    }
  }, [callState.isCallActive, callState.isOnCall, callState.groupCallActive, callState.showJoinButton]);


  // Scope the mobile document scroll-lock (html/body position:fixed) to just this page.
  // ChatPage.css is bundled globally (imported in App.js), so without this class toggle
  // the lock would apply to every route, breaking normal scrolling elsewhere.
  useEffect(() => {
    document.body.classList.add('chat-page-mounted');
    document.documentElement.classList.add('chat-page-mounted');
    return () => {
      document.body.classList.remove('chat-page-mounted');
      document.documentElement.classList.remove('chat-page-mounted');
    };
  }, []);

  // Viewport height management for mobile — simplified
  // 100dvh handles keyboard/browser UI natively; JS only sets --viewport-height as fallback
  // and toggles body.keyboard-open to hide ads
  useEffect(() => {
    const updateViewport = () => {
      // visualViewport.height accounts for both address-bar collapse AND the
      // on-screen keyboard; window.innerHeight only accounts for the former.
      const h = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      document.documentElement.style.setProperty('--viewport-height', `${h}px`);

      const inputFocused = document.activeElement === messageInputRef.current;

      // Detect keyboard for body.keyboard-open class (used only to hide ads).
      // NOTE: with `interactive-widget=resizes-content` on supporting browsers,
      // window.innerHeight shrinks together with visualViewport.height, so this
      // offset can stay ~0 even while the keyboard is open — fall back to
      // "input is focused" as an additional signal.
      if (window.visualViewport) {
        const offset = window.innerHeight - window.visualViewport.height;
        const isKeyboardOpen = offset > 100 || inputFocused;
        if (isKeyboardOpen) {
          document.body.classList.add('keyboard-open');
        } else {
          document.body.classList.remove('keyboard-open');
        }
      }

      // Every actual viewport resize/scroll event (the real signal that the
      // keyboard is animating open/closed) — while the chat input is focused,
      // re-snap the message list to the bottom so the latest message isn't
      // left hidden behind the keyboard. Event-driven, not offset-gated, so
      // it works regardless of whether interactive-widget is honored.
      if (inputFocused) {
        scrollToBottomAfterLayout();
      }
    };

    updateViewport();

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateViewport);
      window.visualViewport.addEventListener('scroll', updateViewport);
    }

    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);

    // Fallback for browsers without visualViewport: detect keyboard via input focus/blur
    const handleInputFocus = () => {
      if (!window.visualViewport) {
        document.body.classList.add('keyboard-open');
        scrollToBottomAfterLayout();
      }
    };

    const handleInputBlur = () => {
      if (!window.visualViewport) {
        document.body.classList.remove('keyboard-open');
      }
    };

    const inputs = document.querySelectorAll('input[type="text"], textarea');
    inputs.forEach(input => {
      input.addEventListener('focus', handleInputFocus);
      input.addEventListener('blur', handleInputBlur);
    });

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateViewport);
        window.visualViewport.removeEventListener('scroll', updateViewport);
      }
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
      inputs.forEach(input => {
        input.removeEventListener('focus', handleInputFocus);
        input.removeEventListener('blur', handleInputBlur);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize connection on mount
  useEffect(() => {
    // Prevent double initialization from React Strict Mode
    if (initializingRef.current || connectionManagerRef.current) {
      console.log('[ChatPage] Skipping duplicate initialization');
      return;
    }
    
    initializingRef.current = true;
    
    const initConnection = async () => {
      try {
        console.log('[ChatPage] Creating ConnectionManager for room:', roomId);
        const conn = new ConnectionManager({ roomId });
        connectionManagerRef.current = conn;
        setConnectionManager(conn);

        // Set up event handlers
        conn.on('open', ({ peerId: newPeerId }) => {
          setPeerId(newPeerId);
          const link = `${window.location.origin}/chat/${roomId}`;
          setRoomLink(link);
          showToast('Connected to room', 'success');
        });

        conn.on('users:update', (updatedUsers) => {
          // Detect join/leave/disconnect for in-chat notifications
          const prev = prevUsersRef.current;
          const prevIds = new Set(prev.filter(u => u.status === 'active').map(u => u.peerId));
          const currActive = updatedUsers.filter(u => u.status === 'active');
          const currIds = new Set(currActive.map(u => u.peerId));
          const myPeerId = sessionStorage.getItem('peerId');

          // New users that joined
          currActive.forEach(u => {
            if (!prevIds.has(u.peerId) && u.peerId !== myPeerId && prev.length > 0) {
              const name = u.peerName || u.userName || u.initial || 'A user';
              setMessages(p => [...p, { type: 'system', data: { text: `${name} joined the room` }, ts: Date.now() }]);
            }
          });

          // Users that left or disconnected
          prev.filter(u => u.status === 'active' && u.peerId !== myPeerId).forEach(u => {
            if (!currIds.has(u.peerId)) {
              const name = u.peerName || u.userName || u.initial || 'A user';
              const leftUser = updatedUsers.find(x => x.peerId === u.peerId);
              const reason = leftUser?.status === 'inactive' ? 'disconnected' : 'left the room';
              setMessages(p => [...p, { type: 'system', data: { text: `${name} ${reason}` }, ts: Date.now() }]);
            }
          });

          prevUsersRef.current = updatedUsers;
          setUsers(updatedUsers);
          // Find current user using sessionStorage (not localStorage - we don't persist)
          const storedPeerId = myPeerId;
          const me = updatedUsers.find(u => u.peerId === storedPeerId);
          if (me) {
            setCurrentUser(me);
            // Sync chatManager current user when room state updates
            if (chatManagerRef.current) {
              chatManagerRef.current.setCurrentUser(me);
            }
            const isUserInitiator = me.userType === 'initiator';
            setIsInitiator(isUserInitiator);
            if (!userName) {
              setUserName(me.peerName || me.userName || '');
            }
            
            // Show SharePopup for initiator when they first join
            if (isUserInitiator && updatedUsers.length === 1 && !roomPhrase) {
              const phrase = roomId.replace(/-/g, ' ');
              setRoomPhrase(phrase);
              setShowSharePopup(true);
            }
            
            // Hide SharePopup when guest joins
            if (isUserInitiator && updatedUsers.length > 1) {
              setShowSharePopup(false);
            }
          }
        });

        conn.on('connection:ready', () => {
          console.log('Connection ready');
        });

        conn.on('error', (error) => {
          // Only show critical errors to user
          if (error && error.type === 'peer-unavailable') {
            console.warn('Peer unavailable (already disconnected)');
            return; // Don't show toast for non-critical errors
          }
          console.error('Connection error:', error);
          showToast('Connection error occurred', 'error');
        });

        // Start connection
        await conn.start();

      } catch (error) {
        console.error('Error initializing connection:', error);
        showToast('Failed to connect to room', 'error');
      }
    };

    initConnection();

    // Cleanup function
    return () => {
      console.log('[ChatPage] Cleanup called');
      if (connectionManagerRef.current) {
        try {
          connectionManagerRef.current.disconnect?.();
          connectionManagerRef.current = null;
        } catch (e) {
          console.error('[ChatPage] Cleanup error:', e);
        }
      }
      initializingRef.current = false;
    };
  }, [roomId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize chat manager when connection is ready
  useEffect(() => {
    if (!connectionManager || !currentUser) return;

    const chat = new ChatManager({ 
      connectionManager, 
      currentUser 
    });
    setChatManager(chat);
    chatManagerRef.current = chat;

    // Set up message handlers
    chat.on('message:received', (msg) => {
      setMessages(prev => [...prev, msg]);
      playSound('ding');
      scrollToBottom();
    });

    chat.on('link:received', (data) => {
      setMessages(prev => [...prev, data.data]);
      const linkEntry = {
        url: data.data.data?.url || data.data.url,
        title: data.data.data?.title || data.data.title,
        description: data.data.data?.description,
        image: data.data.data?.image,
        ts: Date.now()
      };
      setStoredFiles(prev => ({
        ...prev,
        links: [linkEntry, ...prev.links]
      }));
      playSound('ding');
      scrollToBottom();
    });

    chat.on('typing', ({ peerId: typingPeerId, data }) => {
      if (data.data.isTyping) {
        setTypingUsers(prev => {
          if (!prev.find(u => u.peerId === typingPeerId)) {
            const user = users.find(u => u.peerId === typingPeerId);
            return user ? [...prev, user] : prev;
          }
          return prev;
        });
      } else {
        setTypingUsers(prev => prev.filter(u => u.peerId !== typingPeerId));
      }
    });

    // File transfer events - These will be handled by FileTransfer component
    // and it will add messages to chat directly via addFileMessage callback

    return () => {
      if (chat) {
        chat.dispose();
      }
    };
  }, [connectionManager, currentUser, users]);

  // Generate video thumbnail
  const generateVideoThumbnail = (videoUrl, fileId) => {
    const video = document.createElement('video');
    video.src = videoUrl;
    video.crossOrigin = 'anonymous';
    
    video.addEventListener('loadeddata', () => {
      // Seek to 20th frame (assuming 30fps, that's ~0.67 seconds)
      video.currentTime = Math.min(0.67, video.duration / 2);
    });
    
    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        if (blob) {
          const thumbnailUrl = URL.createObjectURL(blob);
          setVideoThumbnails(prev => ({ ...prev, [fileId]: thumbnailUrl }));
        }
      }, 'image/jpeg', 0.8);
    });
    
    video.load();
  };

  // Add or update file message in chat
  const addFileMessage = (fileData) => {
    setMessages(prev => {
      // Check if this file message already exists
      const existingIndex = prev.findIndex(
        msg => msg.type === 'file' && msg.data?.id === fileData.id
      );
      
      const fileMessage = {
        type: 'file',
        user: fileData.from ? users.find(u => u.peerId === fileData.from) || currentUser : currentUser,
        data: fileData,
        ts: existingIndex >= 0 ? prev[existingIndex].ts : Date.now()
      };
      
      if (existingIndex >= 0) {
        // Update existing message
        const newMessages = [...prev];
        newMessages[existingIndex] = fileMessage;
        return newMessages;
      } else {
        // Add new message
        return [...prev, fileMessage];
      }
    });
    
    // Store file in memory based on type
    if (fileData.status === 'completed' && fileData.blobUrl) {
      if (fileData.type?.startsWith('image/')) {
        const fileEntry = {
          url: fileData.blobUrl,
          name: fileData.name,
          type: fileData.type,
          size: fileData.size,
          ts: Date.now()
        };
        setStoredFiles(prev => ({
          ...prev,
          media: [fileEntry, ...prev.media]
        }));
        setMediaGallery(prev => {
          const newGallery = [fileData.blobUrl, ...prev];
          return newGallery.slice(0, 6); // Keep only latest 6
        });
      } else if (fileData.type?.startsWith('video/')) {
        const fileId = `video_${Date.now()}_${Math.random()}`;
        const fileEntry = {
          url: fileData.blobUrl,
          name: fileData.name,
          type: fileData.type,
          size: fileData.size,
          ts: Date.now(),
          fileId: fileId
        };
        setStoredFiles(prev => ({
          ...prev,
          media: [fileEntry, ...prev.media]
        }));
        // Generate video thumbnail
        generateVideoThumbnail(fileData.blobUrl, fileId);
      } else {
        const fileEntry = {
          url: fileData.blobUrl,
          name: fileData.name,
          type: fileData.type,
          size: fileData.size,
          ts: Date.now()
        };
        setStoredFiles(prev => ({
          ...prev,
          files: [fileEntry, ...prev.files]
        }));
      }
    }
    
    scrollToBottom();
  };

  // Add call event message to chat
  const addCallEvent = (eventText, icon = 'fa-phone') => {
    const callMessage = {
      type: 'call-event',
      data: { text: eventText, icon },
      ts: Date.now()
    };
    setMessages(prev => [...prev, callMessage]);
    scrollToBottom();
  };

  // Auto-scroll to bottom
  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Same as scrollToBottom, but re-applied several times while the on-screen
  // keyboard slides in — its animation (~150-350ms) is slower than a couple
  // of animation frames, and --viewport-height / the flex layout keep
  // shrinking during that time, so a single early scroll lands short of the
  // real bottom. Repeating catches the final settled height reliably.
  const scrollToBottomAfterLayout = () => {
    scrollToBottom();
    requestAnimationFrame(scrollToBottom);
    [50, 120, 200, 300, 400].forEach(delay => {
      setTimeout(scrollToBottom, delay);
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Focus username input when editing
  useEffect(() => {
    if (isEditingName && userNameInputRef.current) {
      userNameInputRef.current.focus();
      userNameInputRef.current.select();
    }
  }, [isEditingName]);

  // Hide green tick after 2 seconds
  useEffect(() => {
    if (showNameSavedTick) {
      const timer = setTimeout(() => {
        setShowNameSavedTick(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showNameSavedTick]);

  // Handle message send
  const handleSendMessage = async () => {
    if (!messageInput.trim() || !chatManager) return;

    const text = messageInput.trim();
    
    // Check for link - use cached preview if available
    const linkCheck = haveLink(text);
    if (linkCheck.is_link) {
      // Use existing preview or fetch new
      const metadata = linkPreview || {};
      
      chatManager.sendLink(linkCheck.link, {
        text,
        title: metadata.title || 'No Title',
        description: metadata.description || '',
        image: metadata.image || '',
        url: linkCheck.link
      });
      
      // Add to own messages
      setMessages(prev => [...prev, {
        type: 'link',
        user: currentUser,
        data: {
          text,
          url: linkCheck.link,
          title: metadata.title || 'No Title',
          description: metadata.description || '',
          image: metadata.image || ''
        },
        ts: Date.now()
      }]);
      
      const linkEntry = {
        url: linkCheck.link,
        title: metadata.title || 'No Title',
        description: metadata.description || '',
        image: metadata.image || '',
        ts: Date.now()
      };
      setStoredFiles(prev => ({
        ...prev,
        links: [linkEntry, ...prev.links]
      }));
    } else {
      chatManager.sendText(text);
      setMessages(prev => [...prev, {
        type: 'text',
        user: currentUser,
        data: text,
        ts: Date.now()
      }]);
    }

    setMessageInput('');
    setLinkPreview(null); // Clear preview after sending
    scrollToBottom();
    // Keep the keyboard open after sending by refocusing the input
    messageInputRef.current?.focus();
  };

  // Handle typing indicator
  const handleTyping = debounce(() => {
    if (chatManager) {
      chatManager.sendTyping(true);
      
      // Clear previous timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Stop typing after 3 seconds
      typingTimeoutRef.current = setTimeout(() => {
        chatManager.sendTyping(false);
      }, 3000);
    }
  }, 500);

  // Fetch link preview (debounced)
  const fetchLinkPreview = debounce(async (text) => {
    const linkCheck = haveLink(text);
    if (linkCheck.is_link) {
      setIsFetchingPreview(true);
      try {
        const apiBase = process.env.REACT_APP_API_BASE || '';
        const response = await fetch(`${apiBase}/metadata`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: linkCheck.link })
        });
        const metadata = await response.json();
        
        if (metadata.success) {
          setLinkPreview({
            url: linkCheck.link,
            title: metadata.title || 'No Title',
            description: metadata.description || '',
            image: metadata.image || ''
          });
        }
      } catch (error) {
        console.error('Error fetching link preview:', error);
        setLinkPreview(null);
      } finally {
        setIsFetchingPreview(false);
      }
    } else {
      setLinkPreview(null);
    }
  }, 800);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setMessageInput(text);
    handleTyping();
    
    // Clear previous timeout
    if (linkPreviewTimeoutRef.current) {
      clearTimeout(linkPreviewTimeoutRef.current);
    }
    
    // Fetch link preview after user stops typing
    if (text.trim()) {
      fetchLinkPreview(text.trim());
    } else {
      setLinkPreview(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle emoji select
  const handleEmojiSelect = (emoji) => {
    setMessageInput(prev => prev + emoji);
  };

  // Handle name save (mobile + desktop)
  const handleNameSave = async () => {
    if (!userName.trim() || userName.trim().length < 3 || !connectionManager || !peerId) return;
    
    try {
      const apiBase = process.env.REACT_APP_API_BASE || '';
      await fetch(`${apiBase}/room/${roomId}/update-peer-name`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ peerId, newPeerName: userName.trim() })
      });
      // Sync local state and chatManager immediately
      setUserName(userName.trim());
      if (chatManagerRef.current && currentUser) {
        chatManagerRef.current.setCurrentUser({ ...currentUser, peerName: userName.trim() });
      }
      setShowNameSavedTick(true);
      setIsEditingName(false);
      showToast('Name updated successfully', 'success');
      // Auto-hide the tick after 2 seconds
      setTimeout(() => setShowNameSavedTick(false), 2000);
    } catch (error) {
      console.error('Error updating name:', error);
      showToast('Failed to update name', 'error');
    }
  };

  // Handle leave chat
  const handleLeaveChat = () => {
    showConfirm(
      'Are you sure you want to leave this chat?',
      () => {
        if (connectionManager) {
          connectionManager.disconnect?.();
        }
        navigate('/');
      }
    );
  };

  // Handle image click to open preview
  const handleImageClick = (imageUrl) => {
    const imageIndex = storedFiles.media.findIndex(item => item.url === imageUrl);
    if (imageIndex !== -1) {
      setImagePreviewIndex(imageIndex);
      setShowImagePreview(true);
    } else {
      // Fallback: add as first item and open
      setImagePreviewIndex(0);
      setShowImagePreview(true);
    }
  };

  return (
    <div id="main" className="container-fluid chat-page">
      <div id="chat-container-wrapper" className="chat-container row">
        {/* Left sidebar - User info */}
        <div className="photos-files-container col-md-2 d-none d-md-block">
          <div className="user-info-section">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center flex-grow-1">
                <div className="main-avatar avatar text-white rounded-circle d-flex justify-content-center align-items-center me-2"
                  style={{ backgroundColor: currentUser?.color || '#333', width: '48px', height: '48px', fontSize: '18px', position: 'relative', flexShrink: 0 }}>
                  {currentUser?.initial || 'G'}
                  <span className="status-indicator"></span>
                </div>
                <div>
                  <h6 className="mb-0 fw-bold" style={{ fontSize: '15px' }}>{currentUser?.peerName || currentUser?.userName || 'Guest'}</h6>
                  <small className="text-success" style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}><i className="fas fa-circle" style={{ fontSize: '7px' }}></i> Active</small>
                </div>
              </div>
              <button className="btn btn-danger btn-sm" onClick={handleLeaveChat} style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Leave Room
              </button>
            </div>
          </div>

          {/* Network Strength Monitor */}
          <NetworkMonitor />

          {/* Desktop Sidebar Ad - Left 01 */}
          <div className="mt-3">
            <AdSenseUnit adSlot="1251652312" adFormat="auto" />
          </div>

          {/* Desktop Sidebar Ad - Left 02 */}
          <div className="mt-3">
            <AdSenseUnit adSlot="5426701048" adFormat="auto" />
          </div>
        </div>

        {/* Center chat section */}
        <div id="chat-messages-container" className="col-md-8">
          <div id="chat-main">
            <div className="chat-message-list">
              {/* Combined header row: room name (left), speed (center), call buttons (right) */}
              <div className="chat-header-combined">
                {/* Mobile row 1: room name (left) + speed (right) */}
                <div className="chat-header-mobile-row1 d-md-none">
                  <span className="chat-room-name">Room: {roomId}</span>
                  <div className="chat-bandwidth-mini">
                    <span className="bw-item">↑ {bandwidth.sent}</span>
                    <span className="bw-item">↓ {bandwidth.received}</span>
                    <span className="bw-unit">kbps</span>
                  </div>
                </div>
                {/* Desktop: room name (left) */}
                <div className="chat-header-left d-none d-md-flex">
                  <span className="chat-room-name">Room: {roomId}</span>
                </div>
                {/* Desktop: speed (center) */}
                <div className="chat-header-center d-none d-md-flex">
                  <div className="chat-bandwidth-mini">
                    <span className="bw-item">↑ {bandwidth.sent}</span>
                    <span className="bw-item">↓ {bandwidth.received}</span>
                    <span className="bw-unit">kbps</span>
                  </div>
                </div>
                {/* Desktop + mobile: call buttons (right). Mobile adds sidebar btn (left) */}
                <div className="chat-header-right">
                  {/* Mobile sidebar btn */}
                  <span className="footer-btn mobile-only-btn d-md-none" onClick={() => setShowMobileModal(true)}>
                    <i className="fas fa-bars" style={{ fontSize: '20px', color: '#666' }}></i>
                  </span>
                  <UserList users={users} currentUser={currentUser} className="d-none d-md-flex" />
                  <div className="btn-group call-controls-minimal call-start-buttons" role="group">
                    {!callState.isCallActive && !callState.groupCallActive && !screenState.isSharing && (
                      <>
                        <button
                          className="btn btn-icon"
                          title="Start Video Call"
                          onClick={() => {
                            setShowVideoCall(true);
                            videoCallRef.current?.startVideoCall?.();
                          }}
                        >
                          <i className="fas fa-video"></i>
                        </button>
                        <button
                          className="btn btn-icon"
                          title="Virtual Background"
                          onClick={() => setShowBgSettings(true)}
                        >
                          <i className="fas fa-image"></i>
                        </button>
                        <button
                          className="btn btn-icon"
                          title="Start Audio Call"
                          onClick={() => {
                            setShowVideoCall(true);
                            videoCallRef.current?.startAudioCall?.();
                          }}
                        >
                          <i className="fas fa-phone"></i>
                        </button>
                        <button
                          className="btn btn-icon"
                          title="Start Screen Share"
                          onClick={() => {
                            setShowScreenShare(true);
                            screenShareRef.current?.startScreenShare?.();
                          }}
                        >
                          <i className="fas fa-desktop"></i>
                        </button>
                      </>
                    )}

                    {(callState.showJoinButton && !callState.isOnCall && !callState.isCallActive) && (
                      <button
                        className="btn btn-icon join-group-call"
                        title="Join Group Call"
                        onClick={() => {
                          setShowVideoCall(true);
                          videoCallRef.current?.joinActiveGroupCall?.();
                        }}
                      >
                        <i className="fas fa-sign-in-alt me-1"></i>
                        Join Group Call
                      </button>
                    )}

                    {(callState.isCallActive || callState.isOnCall) && (
                      <>
                        <button
                          className={`btn btn-icon ${callState.videoEnabled ? 'active' : ''}`}
                          title="Toggle Camera"
                          onClick={() => videoCallRef.current?.toggleVideo?.()}
                        >
                          <i className={`fas fa-${callState.videoEnabled ? 'video' : 'video-slash'}`}></i>
                        </button>
                        <button
                          className={`btn btn-icon ${callState.audioEnabled ? 'active' : ''}`}
                          title="Toggle Mic"
                          onClick={() => videoCallRef.current?.toggleAudio?.()}
                        >
                          <i className={`fas fa-${callState.audioEnabled ? 'microphone' : 'microphone-slash'}`}></i>
                        </button>
                        <button
                          className="btn btn-icon"
                          title="Virtual Background"
                          onClick={() => setShowBgSettings(true)}
                        >
                          <i className="fas fa-image"></i>
                        </button>
                        <button
                          className="btn btn-icon danger"
                          title="End Call"
                          onClick={() => {
                            videoCallRef.current?.endCall?.();
                            setShowVideoCall(false);
                          }}
                        >
                          <i className="fas fa-phone-slash"></i>
                        </button>
                      </>
                    )}

                    {screenState.isSharing && (
                      <button
                        className="btn btn-icon danger"
                        title="Stop Screen Share"
                        onClick={() => {
                          screenShareRef.current?.stopScreenShare?.();
                          setShowScreenShare(false);
                        }}
                      >
                        <i className="fas fa-stop"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Video call container - Always mounted to listen for incoming calls */}
              <div className={`video-call-container chat-video-strip video-call-pos-${videoCallPosition} ${!showVideoCall ? 'd-none' : ''}`}>
                {showVideoCall && (
                  <div className="video-call-drag-handle">
                    {videoCallPosition === 'top' ? (
                      <button
                        className="btn btn-sm video-call-pos-btn"
                        title="Move video to bottom"
                        onClick={() => setVideoCallPosition('bottom')}
                      >
                        <i className="fas fa-arrow-down"></i>
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm video-call-pos-btn"
                        title="Move video to top"
                        onClick={() => setVideoCallPosition('top')}
                      >
                        <i className="fas fa-arrow-up"></i>
                      </button>
                    )}
                  </div>
                )}
                <VideoCall 
                  ref={videoCallRef}
                  connectionManager={connectionManager}
                  currentUser={currentUser}
                  users={users}
                  bgSettings={bgSettings}
                  isVisible={showVideoCall}
                  onIncomingCall={({ peerId }) => {
                    console.log('[ChatPage] Incoming call detected, auto-showing video panel');
                    setShowVideoCall(true);
                  }}
                  onCallEvent={addCallEvent}
                  onStateChange={setCallState}
                  onBandwidthUpdate={setBandwidth}
                  hideControls={true}
                />
              </div>

              {/* Screen share container - Always mounted to listen for incoming shares */}
              <div className={`screen-share-container mx-3 ${!showScreenShare ? 'd-none' : ''}`}>
                <ScreenShare
                  ref={screenShareRef}
                  connectionManager={connectionManager}
                  currentUser={currentUser}
                  users={users}
                  isVisible={showScreenShare}
                  onIncomingScreen={({ peerId }) => {
                    console.log('[ChatPage] Incoming screen share detected, auto-showing screen panel');
                    setShowScreenShare(true);
                  }}
                  onCallEvent={addCallEvent}
                  onStateChange={setScreenState}
                  hideControls={true}
                />
              </div>

              {/* Messages list */}
              <div 
                ref={messagesContainerRef}
                className={`chat-messages ${(callState.groupCallActive || callState.isCallActive || callState.isOnCall) && showVideoCall ? 'call-active' : ''}`}
                style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.85), rgba(255,255,255,0.85)), url('/images/chat-bg.png')", backgroundRepeat: 'repeat', backgroundSize: 'cover' }}
              >
                {messages.map((msg, index) => (
                  <MessageBubble
                    key={index}
                    message={msg}
                    currentUser={currentUser}
                    users={users}
                    onMediaClick={handleImageClick}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Mobile sticky call controls */}
              {(callState.groupCallActive || callState.isCallActive || callState.isOnCall) && (
                <div className="mobile-call-controls" aria-hidden="false">
                  <button
                    className={`mcc-btn ${callState.videoEnabled ? 'active' : ''}`}
                    title="Toggle Camera"
                    onClick={() => videoCallRef.current?.toggleVideo?.()}
                  >
                    <i className={`fas fa-${callState.videoEnabled ? 'video' : 'video-slash'}`}></i>
                  </button>
                  <button
                    className={`mcc-btn ${callState.audioEnabled ? 'active' : ''}`}
                    title="Toggle Mic"
                    onClick={() => videoCallRef.current?.toggleAudio?.()}
                  >
                    <i className={`fas fa-${callState.audioEnabled ? 'microphone' : 'microphone-slash'}`}></i>
                  </button>
                  <button
                    className="mcc-btn danger"
                    title="End Call"
                    onClick={() => videoCallRef.current?.endCall?.()}
                  >
                    <i className="fas fa-phone-slash"></i>
                  </button>
                </div>
              )}
            </div>

            {/* Link Preview */}
            {linkPreview && (
              <div className="link-preview-container">
                <div className="link-preview-card">
                  <button 
                    className="link-preview-close" 
                    onClick={() => setLinkPreview(null)}
                    aria-label="Close preview"
                  >
                    ×
                  </button>
                  {linkPreview.image && (
                    <img 
                      src={linkPreview.image} 
                      alt={linkPreview.title}
                      className="link-preview-image"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                  <div className="link-preview-content">
                    <div className="link-preview-title">{linkPreview.title}</div>
                    {linkPreview.description && (
                      <div className="link-preview-description">{linkPreview.description}</div>
                    )}
                    <div className="link-preview-url">{linkPreview.url}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer Section - Sticky at bottom (mobile) */}
            <div className="chat-footer-section">
              {/* Typing indicator - moves with footer on keyboard open */}
              {typingUsers.length > 0 && (
                <TypingIndicator users={typingUsers} />
              )}
              
              {/* Footer input */}
              <div className="chat-footer-wrapper">
                <div className="input-group chat-footer">
                  
                  <div className="footer-group">
                    <input 
                      ref={messageInputRef}
                      type="text" 
                      className="form-control" 
                      placeholder="Type a message"
                      value={messageInput}
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      onFocus={scrollToBottomAfterLayout}
                    />
                    <span className="footer-btn" onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                      <img src="/images/footer-smiley.png" alt="Smiley" />
                    </span>
                    <span className="footer-btn">
                      <label htmlFor="file-upload-input" style={{ cursor: 'pointer', display: 'flex', margin: 0 }}>
                        <img src="/images/footer-attachment.png" alt="Attachment" />
                      </label>
                    </span>
                    <span
                      className="footer-btn send-button"
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={handleSendMessage}
                    >
                      <img src="/images/logo-color-alt.png" alt="Send" />
                    </span>
                  </div>
                </div>
              </div>

              {/* Mobile Banner Ad - Bottom */}
              <div className="chat-footer-ads d-block d-md-none">
                <AdSenseUnit adSlot="8304145821" adFormat="auto" style={{ maxHeight: '200px' }} />
              </div>
            </div>
            
            {/* Emoji Picker */}
            <EmojiPicker
              show={showEmojiPicker}
              onEmojiSelect={handleEmojiSelect}
              onClose={() => setShowEmojiPicker(false)}
            />
            
            {/* File Transfer */}
            <FileTransfer 
              chatManager={chatManager} 
              currentUser={currentUser} 
              onFileMessage={addFileMessage}
            />
          </div>
        </div>
        {/* Right sidebar (media/files) */}
        <div className="photos-files-container col-md-2 d-none d-md-block">
          <h6 className="text-secondary mb-2" style={{ fontSize: '14px', fontWeight: 'bold' }}>YOUR CHAT NAME</h6>
          <div className={`input-group chat-name-group ${isEditingName && !showNameSavedTick && userName.trim().length >= 3 ? 'has-save-btn' : ''}`} style={{ gap: 0 }}>
            <input 
              type="text" 
              className="form-control chat-name-input" 
              style={{ fontSize: '16px', textAlign: 'left', paddingRight: '12px' }}
              value={userName}
              onChange={(e) => { setUserName(e.target.value); setIsEditingName(true); }}
              onClick={(e) => e.target.select()}
              onFocus={(e) => { e.target.select(); setIsEditingName(true); }}
              placeholder="Enter your name"
              ref={userNameInputRef}
            />
            {isEditingName && !showNameSavedTick && userName.trim().length >= 3 && (
              <button
                className="btn btn-success"
                type="button"
                onClick={() => { handleNameSave(); setIsEditingName(false); }}
                style={{ fontWeight: 600 }}
              >
                <i className="fas fa-check"></i> Save
              </button>
            )}
          </div>
          {showNameSavedTick && (
            <div style={{ fontSize: '11px', color: '#28a745', marginTop: '4px' }}>
              <i className="fas fa-check-circle"></i> Saved
            </div>
          )}
          <small className="mt-1 text-primary text-mini" style={{ fontSize: '10px' }}>(If you wish to)</small>

          <div className="photos-section mt-3">
            <h6 className="text-secondary fw-bold" style={{ fontSize: '14px', textAlign: 'left' }}>Photos</h6>
            <div id="image_gallery" className="photos-grid photos-grid-compact">
              {mediaGallery.map((imgUrl, index) => (
                <img 
                  key={index} 
                  src={imgUrl} 
                  className="side-media media-thumbnail-small active" 
                  alt={`media-${index}`}
                  onClick={() => handleImageClick(imgUrl)}
                  style={{ cursor: 'pointer' }}
                />
              ))}
              {[...Array(Math.max(0, 4 - mediaGallery.length))].map((_, index) => (
                <img 
                  key={`placeholder-${index}`} 
                  src="/images/img-bg.jpg" 
                  className="side-media media-thumbnail-small" 
                  alt="placeholder" 
                />
              ))}
            </div>
            {mediaCount > 4 && (
              <div className="text-end mt-1">
                <span className="text-gradient fw-bold" style={{ fontSize: '11px' }}>+{mediaCount - 4} Photos</span>
              </div>
            )}
          </div>

          <div id="gallery-files-section" className="mt-3">
            <div className="files-buttons-section d-flex gap-2">
              
              <button
                className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-between flex-fill px-2"
                onClick={() => { setActiveTab('media'); setShowFilesModal(true); }}
                disabled={mediaCount === 0}
              >
                <span><i className="fas fa-photo-video me-2"></i>Media</span>
                {mediaCount > 0 && <span className="badge bg-primary">{mediaCount}</span>}
              </button>

              <button
                className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-between flex-fill px-2"
                onClick={() => { setActiveTab('links'); setShowFilesModal(true); }}
                disabled={linkCount === 0}
              >
                <span><i className="fas fa-link me-2"></i>Links</span>
                {linkCount > 0 && <span className="badge bg-primary">{linkCount}</span>}
              </button>

              <button
                className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-between flex-fill px-2"
                onClick={() => { setActiveTab('files'); setShowFilesModal(true); }}
                disabled={fileCount === 0}
              >
                <span><i className="fas fa-file me-2"></i>Files</span>
                {fileCount > 0 && <span className="badge bg-primary">{fileCount}</span>}
              </button>

            </div>
          </div>

          {/* Desktop Right Sidebar Ad */}
          <div className="mt-3 d-none d-md-block">
            <AdSenseUnit adSlot="4504501618" adFormat="auto" />
          </div>

        </div>
      </div>

      {/* Mobile Menu Modal */}
      {showMobileModal && (
        <div className="mobile-menu-modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }} onClick={() => setShowMobileModal(false)}>
          <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-menu-header">
              <h5>Room Info</h5>
              <button className="btn-close" onClick={() => setShowMobileModal(false)} style={{ fontSize: '14px' }}></button>
            </div>
            <div className="mobile-menu-body">
              {/* User Info Section */}
              <div className="user-info-section">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center flex-grow-1">
                    <div className="main-avatar avatar text-white rounded-circle d-flex justify-content-center align-items-center me-3"
                      style={{ backgroundColor: currentUser?.color || '#333', width: '48px', height: '48px', fontSize: '18px', position: 'relative', flexShrink: 0 }}>
                      {currentUser?.initial || 'G'}
                      <span className="status-indicator"></span>
                    </div>
                    <div>
                      <h6 className="mb-0 fw-bold" style={{ fontSize: '16px', lineHeight: '1.3' }}>{currentUser?.peerName || currentUser?.userName || 'Guest'}</h6>
                      <small className="text-success" style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '2px' }}><i className="fas fa-circle" style={{ fontSize: '8px' }}></i> Active</small>
                    </div>
                  </div>
                  <button className="btn btn-danger btn-sm" onClick={handleLeaveChat} style={{ fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap', padding: '6px 16px' }}>
                    Leave Room
                  </button>
                </div>
              </div>

              {/* Network Strength Monitor */}
              <NetworkMonitor />

              {/* Chat Name Edit */}
              <h6 className="text-secondary mb-2" style={{ fontSize: '13px', fontWeight: 'bold' }}>YOUR CHAT NAME</h6>
              <div className={`input-group chat-name-group ${isEditingName && !showNameSavedTick && userName.trim().length >= 3 ? 'has-save-btn' : ''}`} style={{ gap: 0 }}>
                <input
                  type="text"
                  className="form-control chat-name-input"
                  value={userName}
                  onChange={(e) => { setUserName(e.target.value); setIsEditingName(true); }}
                  onClick={(e) => e.target.select()}
                  onFocus={(e) => { e.target.select(); setIsEditingName(true); }}
                  onKeyPress={(e) => e.key === 'Enter' && handleNameSave()}
                  inputMode="text"
                  style={{ fontSize: '16px', paddingRight: '12px' }}
                  ref={mobileNameInputRef}
                />
                {isEditingName && !showNameSavedTick && userName.trim().length >= 3 && (
                  <button
                    className="btn btn-success"
                    type="button"
                    onClick={() => { handleNameSave(); setIsEditingName(false); }}
                    style={{ fontWeight: 600 }}
                  >
                    <i className="fas fa-check"></i> Save
                  </button>
                )}
              </div>
              {showNameSavedTick && (
                <div style={{ fontSize: '11px', color: '#28a745', marginTop: '4px' }}>
                  <i className="fas fa-check-circle"></i> Saved
                </div>
              )}
              <small className="mt-1 text-primary text-mini" style={{ fontSize: '11px' }}>(If you wish to)</small>

              {/* Photos Grid */}
              <div className="photos-section">
                <h6 className="text-secondary fw-bold" style={{ fontSize: '13px', textAlign: 'left' }}>Photos</h6>
                <div className="photos-grid photos-grid-compact">
                  {mediaGallery.map((imgUrl, index) => (
                    <img
                      key={index}
                      src={imgUrl}
                      className="side-media media-thumbnail-small active"
                      alt={`media-${index}`}
                      onClick={() => handleImageClick(imgUrl)}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                  {[...Array(Math.max(0, 4 - mediaGallery.length))].map((_, index) => (
                    <img
                      key={`placeholder-${index}`}
                      src="/images/img-bg.jpg"
                      className="side-media media-thumbnail-small"
                      alt="placeholder"
                    />
                  ))}
                </div>
                {mediaCount > 4 && (
                  <div className="text-end mt-1">
                    <span className="text-gradient fw-bold" style={{ fontSize: '11px' }}>+{mediaCount - 4} Photos</span>
                  </div>
                )}
              </div>

              {/* Media/Links/Files Buttons */}
              <div className="files-buttons-section d-flex">
                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-between flex-fill px-2"
                    onClick={() => { setActiveTab('media'); setShowFilesModal(true); setShowMobileModal(false); }}
                    disabled={mediaCount === 0}
                  >
                    <span><i className="fas fa-photo-video me-1"></i>Media</span>
                    {mediaCount > 0 && <span className="badge bg-primary">{mediaCount}</span>}
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-between flex-fill px-2"
                    onClick={() => { setActiveTab('links'); setShowFilesModal(true); setShowMobileModal(false); }}
                    disabled={linkCount === 0}
                  >
                    <span><i className="fas fa-link me-1"></i>Links</span>
                    {linkCount > 0 && <span className="badge bg-primary">{linkCount}</span>}
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm d-flex align-items-center justify-content-between flex-fill px-2"
                    onClick={() => { setActiveTab('files'); setShowFilesModal(true); setShowMobileModal(false); }}
                    disabled={fileCount === 0}
                  >
                    <span><i className="fas fa-file me-1"></i>Files</span>
                    {fileCount > 0 && <span className="badge bg-primary">{fileCount}</span>}
                  </button>
                </div>
            </div>
          </div>
        </div>
      )}

      {/* Files Modal */}
      {showFilesModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={() => setShowFilesModal(false)}>
          <div className="modal-dialog modal-dialog-centered files-modal-dialog" style={{ maxWidth: '1200px', width: '90%' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-content files-modal-content">
              <button 
                className="files-modal-close"
                onClick={() => setShowFilesModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
              
              <div className="files-modal-tabs">
                <button 
                  className={`files-modal-tab ${activeTab === 'media' ? 'active' : ''}`}
                  onClick={() => setActiveTab('media')}
                  disabled={mediaCount === 0}
                >
                  <span>Media</span>
                  <span className="files-modal-badge">{mediaCount}</span>
                </button>
                <button 
                  className={`files-modal-tab ${activeTab === 'links' ? 'active' : ''}`}
                  onClick={() => setActiveTab('links')}
                  disabled={linkCount === 0}
                >
                  <span>Links</span>
                  <span className="files-modal-badge">{linkCount}</span>
                </button>
                <button 
                  className={`files-modal-tab ${activeTab === 'files' ? 'active' : ''}`}
                  onClick={() => setActiveTab('files')}
                  disabled={fileCount === 0}
                >
                  <span>Files</span>
                  <span className="files-modal-badge">{fileCount}</span>
                </button>
              </div>
              
              <div className="files-modal-body">
                {activeTab === 'media' && (
                  <div className="files-modal-grid">
                    {storedFiles.media.length === 0 ? (
                      <p className="text-muted text-center">No media files yet</p>
                    ) : (
                      storedFiles.media.map((file, index) => (
                        <div key={index} className="files-modal-card">
                          {file.type?.startsWith('image/') ? (
                            <div className="files-modal-image-wrapper">
                              <img 
                                src={file.url} 
                                alt={file.name} 
                                className="files-modal-image"
                                onClick={() => handleImageClick(file.url)}
                              />
                              <div className="files-modal-overlay">
                                <a href={file.url} download={file.name} className="files-modal-download-btn">
                                  <i className="fas fa-download"></i>
                                </a>
                              </div>
                            </div>
                          ) : file.type?.startsWith('video/') ? (
                            <div className="files-modal-image-wrapper" onClick={() => handleImageClick(file.url)}>
                              {videoThumbnails[file.fileId] ? (
                                <img src={videoThumbnails[file.fileId]} alt={file.name} className="files-modal-image" />
                              ) : (
                                <video src={file.url} className="files-modal-image" muted />
                              )}
                              <div className="files-modal-overlay">
                                <div className="files-modal-play-icon">
                                  <i className="fas fa-play-circle"></i>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="files-modal-file-icon">
                              <i className="fas fa-file"></i>
                            </div>
                          )}
                          <div className="files-modal-filename">{file.name}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {activeTab === 'links' && (
                  <div className="files-modal-links">
                    {storedFiles.links.length === 0 ? (
                      <p className="text-muted text-center">No links yet</p>
                    ) : (
                      storedFiles.links.map((link, index) => (
                        <div key={index} className="files-modal-link-card">
                          {link.image && (
                            <img src={link.image} alt={link.title} className="files-modal-link-image" />
                          )}
                          <h6 className="files-modal-link-title">{link.title || 'Untitled'}</h6>
                          {link.description && (
                            <p className="files-modal-link-desc">{link.description}</p>
                          )}
                          <a href={link.url} target="_blank" rel="noopener noreferrer" className="files-modal-link-btn">
                            <i className="fas fa-external-link-alt me-1"></i>Open Link
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                )}
                {activeTab === 'files' && (
                  <div className="files-modal-files">
                    {storedFiles.files.length === 0 ? (
                      <p className="text-muted text-center">No files yet</p>
                    ) : (
                      storedFiles.files.map((file, index) => (
                        <div key={index} className="files-modal-file-card">
                          <div className="files-modal-file-info">
                            <i className="fas fa-file files-modal-file-icon"></i>
                            <div className="files-modal-file-details">
                              <div className="files-modal-file-name">{file.name}</div>
                              <div className="files-modal-file-size">{file.size}</div>
                            </div>
                          </div>
                          <a href={file.url} download={file.name} className="files-modal-download-btn">
                            <i className="fas fa-download"></i>
                          </a>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Popup - Only for initiators */}
      {showSharePopup && (
        <SharePopup 
          link={roomLink}
          phrase={roomPhrase}
          onClose={() => setShowSharePopup(false)}
        />
      )}

      {/* Background Settings Panel */}
      {showBgSettings && (
        <BackgroundSettingsPanel
          onClose={() => setShowBgSettings(false)}
          onChange={(settings) => setBgSettings(settings)}
        />
      )}

      {/* Image Preview Modal */}
      {showImagePreview && (
        <ImagePreviewModal
          images={storedFiles.media}
          initialIndex={imagePreviewIndex}
          onClose={() => setShowImagePreview(false)}
        />
      )}
    </div>
  );
}